import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { loginAdmin, verifyMfaToken } from '../services/api';
import MfaLogin from '../components/MfaLogin';
import MfaSetup from '../components/MfaSetup';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mfaStep, setMfaStep] = useState(null); // null | 'login' | 'setup'
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await loginAdmin(email.trim().toLowerCase(), password);
      if (result.success) {
        if (result.requiresMfa) {
          setMfaStep('login');
        } else {
          navigate('/');
        }
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSuccess = (token, user) => {
    localStorage.setItem('authToken', token);
    localStorage.setItem('adminUser', JSON.stringify(user));
    navigate('/');
  };

  const handleMfaCancel = () => {
    setMfaStep(null);
    setError('');
  };

  const handleMfaSetupComplete = () => {
    setMfaStep(null);
    setError('');
    // Optionally show success message or auto-continue
  };

  if (mfaStep === 'login') {
    return (
      <MfaLogin
        email={email}
        onSuccess={handleMfaSuccess}
        onCancel={handleMfaCancel}
      />
    );
  }

  if (mfaStep === 'setup') {
    return (
      <MfaSetup
        adminEmail={email}
        onEnabled={handleMfaSetupComplete}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-900 to-primary-700 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-900 tracking-wide">CleoCure</h1>
          <p className="text-gray-500 mt-1 text-sm">Admin Dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@cleocure.com"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1.5">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none transition"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary-900 text-white py-3 rounded-xl hover:bg-primary-800 transition font-medium disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => setMfaStep('setup')}
              className="text-xs text-primary-600 hover:text-primary-700 transition"
            >
              Enable MFA for this account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
